import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardShell from '@/components/DashboardShell';
import api, { getImageUrl, PLACEHOLDER_IMAGE } from '@/api/client';
import { Button } from '@/components/ui/button';
import { PlusCircle, Pencil, Trash2, Eye } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';

const STATUS_BADGE = {
  active:         { bg: '#D1FAE5', color: '#065F46', label: 'Live' },
  draft:          { bg: '#F3F4F6', color: '#374151', label: 'Draft' },
  sold:           { bg: '#EDE9FE', color: '#5B21B6', label: 'Sold' },
  pending_review: { bg: '#FEF3C7', color: '#92400E', label: 'Under Review' },
  rejected:       { bg: '#FEE2E2', color: '#991B1B', label: 'Rejected' },
};

export default function MyListings() {
  const navigate = useNavigate();
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteId, setDeleteId] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const fetchListings = () => {
    setLoading(true);
    api.get('/my/listings').then(r => setListings(r.data)).catch(() => {}).finally(() => setLoading(false));
  };

  useEffect(() => { fetchListings(); }, []);

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await api.delete(`/my/listings/${deleteId}`);
      toast.success('Listing deleted');
      setDeleteId(null);
      fetchListings();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete');
    } finally {
      setDeleting(false);
    }
  };

  const handleSubmit = async (id) => {
    try {
      await api.post(`/my/listings/${id}/submit`);
      toast.success('Listing submitted for review');
      fetchListings();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to submit');
    }
  };

  const formatPrice = (price, currency) => {
    if (!price) return 'POA';
    const sym = currency === 'GBP' ? '£' : currency === 'EUR' ? '€' : '$';
    return `${sym}${Number(price).toLocaleString()}`;
  };

  return (
    <DashboardShell title="My Listings">
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <p className="text-sm" style={{ color: 'var(--text-muted)' }}>{listings.length} listing{listings.length !== 1 ? 's' : ''}</p>
          <Button onClick={() => navigate('/dashboard/listings/create')} data-testid="member-create-listing-button" style={{ backgroundColor: 'var(--text)', color: 'var(--bg)', borderRadius: '10px' }}>
            <PlusCircle size={15} className="mr-2" /> Create Listing
          </Button>
        </div>

        {loading ? (
          <div className="space-y-3">{[1,2,3].map(i => <div key={i} className="h-20 rounded-xl" style={{ backgroundColor: 'var(--bg-subtle)' }} />)}</div>
        ) : listings.length === 0 ? (
          <div className="text-center py-16 rounded-xl border" style={{ borderColor: 'var(--border-clr)', backgroundColor: 'var(--surface)' }}>
            <p className="text-lg mb-2" style={{ fontFamily: 'var(--font-serif)', color: 'var(--text)' }}>No listings yet</p>
            <p className="text-sm mb-6" style={{ color: 'var(--text-muted)' }}>Create your first listing to start selling.</p>
            <Button onClick={() => navigate('/dashboard/listings/create')} style={{ backgroundColor: 'var(--text)', color: 'var(--bg)', borderRadius: '10px' }}><PlusCircle size={14} className="mr-2" />Create Listing</Button>
          </div>
        ) : (
          <div className="space-y-3">
            {listings.map(l => {
              const badge = STATUS_BADGE[l.status] || STATUS_BADGE.draft;
              const canEdit = ['draft', 'rejected'].includes(l.status);
              const canView = l.status === 'active';
              const canSubmit = l.status === 'draft';
              return (
                <div key={l.id} className="flex items-center gap-4 p-4 rounded-xl border" style={{ borderColor: 'var(--border-clr)', backgroundColor: 'var(--surface)' }}>
                  <div className="w-14 h-14 rounded-lg overflow-hidden flex-shrink-0" style={{ backgroundColor: 'var(--bg-subtle)' }}>
                    <img src={l.images?.[0] ? getImageUrl(l.images[0]) : PLACEHOLDER_IMAGE} alt="" className="w-full h-full object-cover" onError={e => { e.target.src = PLACEHOLDER_IMAGE; }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate" style={{ color: 'var(--text)' }}>{l.title}</p>
                    <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{l.category_name} · {l.type_name} · {formatPrice(l.price, l.currency)}</p>
                    {l.status === 'pending_review' && (
                      <p className="text-xs mt-0.5" style={{ color: '#92400E' }}>Awaiting admin approval — you will be notified by email</p>
                    )}
                    {l.status === 'rejected' && l.admin_notes && (
                      <p className="text-xs mt-0.5" style={{ color: '#991B1B' }}>Reason: {l.admin_notes}</p>
                    )}
                  </div>
                  <span className="text-xs px-2.5 py-1 rounded-full flex-shrink-0 font-medium" style={{ backgroundColor: badge.bg, color: badge.color }}>{badge.label}</span>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    {canView && (
                      <button onClick={() => window.open(`/listings/${l.id}`, '_blank')} className="p-1.5 rounded hover:bg-gray-100" title="View live" data-testid={`view-listing-${l.id}`}>
                        <Eye size={15} style={{ color: 'var(--text-muted)' }} />
                      </button>
                    )}
                    {canSubmit && (
                      <button
                        onClick={() => handleSubmit(l.id)}
                        className="text-xs px-2.5 py-1 rounded-lg border font-medium transition-colors"
                        style={{ borderColor: 'var(--border-strong)', color: 'var(--text)' }}
                        data-testid={`submit-listing-${l.id}`}
                        title="Submit for review"
                      >
                        Submit
                      </button>
                    )}
                    {canEdit && (
                      <button onClick={() => navigate(`/dashboard/listings/${l.id}/edit`)} className="p-1.5 rounded hover:bg-gray-100" title="Edit" data-testid={`edit-listing-${l.id}`}>
                        <Pencil size={15} style={{ color: 'var(--text-muted)' }} />
                      </button>
                    )}
                    <button onClick={() => setDeleteId(l.id)} className="p-1.5 rounded hover:bg-red-50" title="Delete" data-testid={`delete-listing-${l.id}`}>
                      <Trash2 size={15} style={{ color: 'var(--danger)' }} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <Dialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Delete Listing?</DialogTitle></DialogHeader>
          <p className="text-sm" style={{ color: 'var(--text-muted)' }}>This action cannot be undone. The listing will be permanently removed.</p>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDeleteId(null)}>Cancel</Button>
            <Button onClick={handleDelete} disabled={deleting} style={{ backgroundColor: 'var(--danger)', color: 'white' }}>{deleting ? 'Deleting...' : 'Delete'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardShell>
  );
}
